import React from 'react';
import { useQuery } from '@apollo/client';
import moment from 'moment';
import { getLocationSalesDataQuery } from '../ServedOrderSection/queries';
import { getLocationDeliverectSalesDataQuery } from '../DeliverectOrderSection/queries';
import { getLocationTakeawaySalesDataQuery } from '../TakeawayOrderSection/queries';
import OrderStatsSection from '../../../../../components/OrderStatsSection';

interface Props {
  locationId: string;
  dateRange: { startDate: Date | null; endDate: Date | null };
  customDateValid: boolean;
  classes: any;
}

const CombinedOrderSection: React.FC<Props> = ({ locationId, dateRange, customDateValid, classes }) => {
  const { data: servedData, loading: loadingServed } = useQuery(
    getLocationSalesDataQuery(
      locationId,
      dateRange.startDate ? moment(dateRange.startDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss') : '',
      dateRange.endDate ? moment(dateRange.endDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss') : ''
    ),
    {
      fetchPolicy: 'no-cache',
      skip: !locationId || !customDateValid,
    }
  );
  const { data: deliverectData, loading: loadingDeliverect } = useQuery(
    getLocationDeliverectSalesDataQuery(
      locationId,
      dateRange.startDate ? moment(dateRange.startDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss') : '',
      dateRange.endDate ? moment(dateRange.endDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss') : ''
    ),
    {
      fetchPolicy: 'no-cache',
      skip: !locationId || !customDateValid,
    }
  );
  const { data: takeawayData, loading: loadingTakeaway } = useQuery(
    getLocationTakeawaySalesDataQuery(
      locationId,
      dateRange.startDate ? moment(dateRange.startDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss') : '',
      dateRange.endDate ? moment(dateRange.endDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss') : ''
    ),
    {
      fetchPolicy: 'no-cache',
      skip: !locationId || !customDateValid,
    }
  );

  const loading = loadingServed || loadingDeliverect || loadingTakeaway;

  const servedOrders = (servedData?.locations_by_pk?.orders_served || []).map((o: any) => ({ ...o, _type: 'served' }));
  const deliverectOrders = (deliverectData?.locations_by_pk?.orders_deliverect || []).map((o: any) => ({ ...o, _type: 'deliverect' }));
  const takeawayOrders = (takeawayData?.locations_by_pk?.orders_takeaway || []).map((o: any) => ({ ...o, _type: 'takeaway' }));
  const allOrders = [...servedOrders, ...deliverectOrders, ...takeawayOrders];

  const totalSales =
    (servedData?.locations_by_pk?.orders_served_aggregate?.aggregate?.sum?.total || 0) +
    (deliverectData?.locations_by_pk?.orders_deliverect_aggregate?.aggregate?.sum?.total || 0) +
    (takeawayData?.locations_by_pk?.orders_takeaway_aggregate?.aggregate?.sum?.total || 0);
  const totalOrders =
    (servedData?.locations_by_pk?.orders_served_aggregate?.aggregate?.count || 0) +
    (deliverectData?.locations_by_pk?.orders_deliverect_aggregate?.aggregate?.count || 0) +
    (takeawayData?.locations_by_pk?.orders_takeaway_aggregate?.aggregate?.count || 0);
  const avgOrderValue = totalOrders > 0 ? totalSales / totalOrders : 0;

  const salesByDate: Record<string, number> = {};
  allOrders.forEach((order: any) => {
    const date = moment(order.created_at).format('YYYY-MM-DD');
    if (!salesByDate[date]) salesByDate[date] = 0;
    salesByDate[date] += order.total || 0;
  });

  const ordersCountByDate: Record<string, number> = {};
  allOrders.forEach((order: any) => {
    const date = moment(order.created_at).format('YYYY-MM-DD');
    if (!ordersCountByDate[date]) ordersCountByDate[date] = 0;
    ordersCountByDate[date] += 1;
  });

  const coversDist: Record<string, number> = {};
  servedOrders.forEach((order: any) => {
    const covers = order.covers || 0;
    coversDist[covers] = (coversDist[covers] || 0) + 1;
  });
  
  let quickSale = 0,
    dineIn = 0;
  servedOrders.forEach((order: any) => {
    if (order.quick_sale) quickSale++;
    else dineIn++;
  });

  const metrics = {
    totalSales,
    totalOrders,
    avgOrderValue,
  };

  const tableColumns = [
    { key: '#', label: '#', render: (_: any, idx: number) => idx + 1 },
    { key: 'order_number', label: 'Order Number' },
    { key: 'created_at', label: 'Date', render: (order: any) => moment(order.created_at).format('YYYY-MM-DD HH:mm') },
    { key: 'total', label: 'Total', render: (order: any) => `£${(order.total / 100).toFixed(2)}` },
    { key: '_type', label: 'Type', render: (order: any) => order._type },
    { key: 'status', label: 'Status', render: (order: any) => order.status || order.deliverect_status || '-' },
  ];

  return (
    <OrderStatsSection
      loading={loading}
      metrics={metrics}
      salesByDate={salesByDate}
      ordersCountByDate={ordersCountByDate}
      coversDist={coversDist}
      quickSale={quickSale}
      dineIn={dineIn}
      orders={allOrders.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())}
      tableColumns={tableColumns}
      classes={classes}
    />
  );
};

export default CombinedOrderSection;
